import React, { useCallback, useEffect, useMemo, useState } from "react";
import { analyticsApi } from "../services/api";
import {
  Activity,
  AlertCircle,
  BarChart3,
  Clock,
  Database,
  RefreshCw,
  TrendingUp,
  Zap,
} from "lucide-react";

const RANGE_OPTIONS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "Last 7 Days" },
  { key: "30d", label: "Last 30 Days" },
  { key: "custom", label: "Custom Range" },
];

const DEFAULT_MODEL_NAME = "gpt-5-nano";

const formatNumber = (value) => (Number.isFinite(value) ? value.toLocaleString() : "0");
const formatDateLabel = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toDateString();
};

const SectionCard = ({ title, children, icon: Icon }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
    <div className="flex items-center gap-2 mb-3">
      {Icon ? <Icon className="w-5 h-5 text-blue-600" /> : null}
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    </div>
    {children}
  </div>
);

const StatTile = ({ label, value, helper, icon: Icon }) => (
  <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
    {Icon && (
      <div className="flex justify-end mb-2">
        <Icon className="w-5 h-5 text-blue-600" />
      </div>
    )}
    <p className="text-xs text-gray-600 mb-1">{label}</p>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    {helper ? <p className="text-xs text-gray-600 mt-1">{helper}</p> : null}
  </div>
);

const VerticalBar = ({ label, value, max, color = "bg-blue-500" }) => {
  const height = max > 0 ? `${Math.round((value / max) * 100)}%` : "0%";
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-full h-48 bg-gray-100 rounded-t-lg overflow-hidden flex items-end">
        <div
          className={`w-full ${color} rounded-t-lg transition-all duration-300`}
          style={{ height }}
        ></div>
      </div>
      <div className="mt-2 text-center">
        <p className="text-xs text-gray-600 font-medium truncate max-w-full px-1">{label}</p>
        <p className="text-sm font-semibold text-gray-900">{formatNumber(value)}</p>
      </div>
    </div>
  );
};

const DateFilter = ({ selected, onChange, customFrom, customTo, onCustomChange }) => (
  <div className="flex flex-wrap gap-2 items-center">
    {RANGE_OPTIONS.map((option) => (
      <button
        key={option.key}
        onClick={() => onChange(option.key)}
        className={`px-3 py-2 rounded-md border text-sm ${
          selected === option.key
            ? "bg-blue-600 text-white border-blue-600"
            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
        }`}
      >
        {option.label}
      </button>
    ))}
    {selected === "custom" ? (
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={customFrom}
          onChange={(e) => onCustomChange(e.target.value, customTo)}
          className="px-3 py-2 border rounded-md text-sm"
        />
        <span className="text-gray-500 text-sm">to</span>
        <input
          type="date"
          value={customTo}
          onChange={(e) => onCustomChange(customFrom, e.target.value)}
          className="px-3 py-2 border rounded-md text-sm"
        />
      </div>
    ) : null}
  </div>
);

const DetailedLLMAnalytics = () => {
  const [range, setRange] = useState("7d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [llm, setLlm] = useState(null);

  const params = useMemo(() => {
    const base = { range };
    if (range === "custom") {
      return { ...base, from: customFrom, to: customTo };
    }
    return base;
  }, [range, customFrom, customTo]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const llmRes = await analyticsApi.getLLM(params);
      setLlm(llmRes.data);
    } catch (err) {
      console.error("LLM Analytics fetch error:", err);
      const errorMessage =
        err?.response?.data?.error || err?.message || "Failed to load LLM analytics";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    if (range === "custom" && (!customFrom || !customTo)) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [fetchData, range, customFrom, customTo]);

  const modelsByUsage = useMemo(() => {
    if (!llm?.byModel) {
      return [];
    }
    const models = Array.isArray(llm.byModel) ? [...llm.byModel] : [];
    return models.sort((a, b) => {
      const aIsDefault = a.model === DEFAULT_MODEL_NAME;
      const bIsDefault = b.model === DEFAULT_MODEL_NAME;
      if (aIsDefault !== bIsDefault) {
        return aIsDefault ? -1 : 1;
      }
      return b.tokensIn + b.tokensOut - (a.tokensIn + a.tokensOut);
    });
  }, [llm]);

  const dailyPeak = llm?.daily?.reduce((max, row) => Math.max(max, row.calls), 0) || 0;
  const totalTokens = (llm?.tokensIn ?? 0) + (llm?.tokensOut ?? 0);
  const avgTokensPerCall =
    llm?.totalCalls && llm.totalCalls > 0 ? Math.round(totalTokens / llm.totalCalls) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-10 w-10 border-4 border-blue-200 border-t-blue-600 rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-center rounded-lg p-6">
        <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
        <p className="text-red-700">{error}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md inline-flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" /> Retry
        </button>
      </div>
    );
  }

  if (!llm) {
    return (
      <div className="bg-blue-50 border border-blue-200 text-center rounded-lg p-6">
        <Activity className="w-10 h-10 text-blue-500 mx-auto mb-3" />
        <p className="text-blue-700">No LLM analytics data available yet.</p>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md inline-flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" /> Load Data
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Detailed LLM Analytics</h2>
          <p className="text-sm text-gray-600">
            Comprehensive view of LLM usage, tokens, and costs. Default model:{" "}
            <span className="font-semibold text-gray-900">{DEFAULT_MODEL_NAME}</span>.
          </p>
        </div>
        <DateFilter
          selected={range}
          onChange={setRange}
          customFrom={customFrom}
          customTo={customTo}
          onCustomChange={(from, to) => {
            setCustomFrom(from);
            setCustomTo(to);
          }}
        />
      </div>

      {/* Overview Statistics */}
      <SectionCard title="LLM Usage Overview" icon={BarChart3}>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <StatTile
            label="Total LLM Calls"
            value={formatNumber(llm.totalCalls)}
            icon={Activity}
            helper={`Across all models`}
          />
          <StatTile
            label="Total Tokens"
            value={formatNumber(totalTokens)}
            icon={Database}
            helper={`${formatNumber(llm.tokensIn)} in / ${formatNumber(llm.tokensOut)} out`}
          />
          <StatTile
            label="Avg Tokens/Call"
            value={formatNumber(avgTokensPerCall)}
            icon={TrendingUp}
            helper="Average per request"
          />
          <StatTile
            label="Active Models"
            value={llm.byModel?.length || 0}
            icon={Zap}
            helper="Unique models used"
          />
          <StatTile
            label="Default Model"
            value={DEFAULT_MODEL_NAME}
            icon={Zap}
            helper="Current primary model"
          />
        </div>
      </SectionCard>

      {/* Usage by Model */}
      <SectionCard title="Usage by Model" icon={Database}>
        <div className="space-y-4">
          {modelsByUsage.length > 0 ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {modelsByUsage.map((model) => (
                  <VerticalBar
                    key={model.model}
                    label={model.model}
                    value={model.tokensIn + model.tokensOut}
                    max={Math.max(...modelsByUsage.map((row) => row.tokensIn + row.tokensOut), 0)}
                    color={model.model === DEFAULT_MODEL_NAME ? "bg-emerald-500" : "bg-blue-500"}
                  />
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                {modelsByUsage.map((model) => (
                  <div
                    key={model.model}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{model.model}</h4>
                      {model.model === DEFAULT_MODEL_NAME ? (
                        <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">
                          Default
                        </span>
                      ) : null}
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Calls:</span>
                        <span className="font-semibold text-gray-900">
                          {formatNumber(model.calls)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tokens In:</span>
                        <span className="font-semibold text-blue-600">
                          {formatNumber(model.tokensIn)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tokens Out:</span>
                        <span className="font-semibold text-purple-600">
                          {formatNumber(model.tokensOut)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Tokens:</span>
                        <span className="font-semibold text-gray-900">
                          {formatNumber(model.tokensIn + model.tokensOut)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Avg/Call:</span>
                        <span className="font-semibold text-gray-900">
                          {formatNumber(
                            model.calls
                              ? Math.round((model.tokensIn + model.tokensOut) / model.calls)
                              : 0,
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">No model usage data available.</p>
          )}
        </div>
      </SectionCard>

      {/* Daily Breakdown */}
      <SectionCard title="Daily LLM Calls" icon={Clock}>
        <div className="space-y-4">
          {llm.daily && llm.daily.length > 0 ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                {llm.daily.map((row) => (
                  <VerticalBar
                    key={row.date}
                    label={formatDateLabel(row.date)}
                    value={row.calls}
                    max={dailyPeak}
                    color="bg-purple-500"
                  />
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                {llm.daily.map((row) => (
                  <div
                    key={row.date}
                    className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4"
                  >
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {formatDateLabel(row.date)}
                    </h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Calls:</span>
                        <span className="font-semibold text-gray-900">
                          {formatNumber(row.calls)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tokens In:</span>
                        <span className="font-semibold text-blue-600">
                          {formatNumber(row.tokensIn)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tokens Out:</span>
                        <span className="font-semibold text-purple-600">
                          {formatNumber(row.tokensOut)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total:</span>
                        <span className="font-semibold text-gray-900">
                          {formatNumber(row.tokensIn + row.tokensOut)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">No daily data available.</p>
          )}
        </div>
      </SectionCard>
    </div>
  );
};

export default DetailedLLMAnalytics;
